"use client";

import { ArrowRight, LayoutGrid } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ArchiveCurrent() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const archiveItems = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/runway_archive_01.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/runway_archive_01.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/runway_archive_01.png-wvc-sizes",
    alt: "Runway lookbook photography",
    label: "ST_COL_01",
    category: "Runway",
    position: "top-4 left-4 md:top-0 md:left-12",
    size: "w-32 h-40 md:w-44 md:h-56",
    rotation: "-rotate-3",
    delay: "0ms"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/draping_archive_02.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/draping_archive_02.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/draping_archive_02.webp-wvc-sizes",
    alt: "Behind the scenes fabric draping",
    label: "ST_TECH_04",
    category: "Draping",
    position: "top-8 right-2 md:top-16 md:right-8",
    size: "w-28 h-36 md:w-40 md:h-52",
    rotation: "rotate-2",
    delay: "150ms"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/blueprint_archive_03.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/blueprint_archive_03.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/blueprint_archive_03.png-wvc-sizes",
    alt: "Technical sketch blueprint",
    label: "ST_SPEC_09",
    category: "Blueprint",
    position: "top-24 left-2 md:top-64 md:left-4",
    size: "w-36 h-44 md:w-48 md:h-60",
    rotation: "rotate-6",
    delay: "300ms"
  }];
  return <section data-section-id="477" id="archive-current" className="relative min-h-screen w-full overflow-hidden bg-background py-24 md:py-32 lg:flex lg:items-center dark">
      {}
      <div className="absolute top-12 right-12 hidden md:block opacity-20">
        <div className="grid grid-cols-4 gap-4">
          {[...Array(16)].map((_, i) => <div key={i} className="h-1 w-1 bg-foreground rounded-lg" />)}
        </div>
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:gap-8 md:px-12 lg:px-16">
        {}
        <div ref={contentRef} className={`md:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="mb-8 flex items-center gap-4">
            <div className="flex items-center">
              <div className="h-1 w-1 bg-accent-foreground rounded-lg" />
              <div className="h-px w-12 bg-foreground/20" />
            </div>
            <span className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-foreground/60">
              Living Archive — Vol. 04
            </span>
          </div>

          <h1 className="font-serif font-light text-foreground leading-[0.9] tracking-tighter text-[64px] sm:text-[80px] md:text-[96px] lg:text-[110px] xl:text-[124px]">
            <span className="block">Architectural</span>
            <span className="relative inline-block italic font-normal text-accent-foreground">
              garments
              <span className="absolute bottom-4 left-0 h-[1px] w-full bg-accent-foreground/40" />
            </span>
            <span className="block">for the void.</span>
          </h1>

          <p className="mt-10 max-w-xl text-lg leading-relaxed text-foreground/70 font-default md:text-xl">
            Studio Thorne operates at the intersection of clinical engineering and visceral high-fashion. Our archive documents the evolution of form, from raw textile sourcing to the final technical blueprint.
          </p>

          <div className="mt-12 flex flex-col gap-5 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="h-16 rounded-lg bg-foreground px-10 text-base font-medium text-background hover:bg-accent-foreground transition-all duration-500">
              <Link to="/CollectionsPage">Download Line Sheet</Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="group h-16 rounded-lg border-foreground/20 px-8 text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-500">
              <Link to="/AtelierPage" className="inline-flex items-center gap-3">
                Explore Atelier
                <ArrowRight className="size-5 transition-transform duration-500 group-hover:translate-x-2" />
              </Link>
            </Button>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8 text-[10px] font-mono uppercase tracking-[0.25em] text-foreground/40">
            <div className="flex items-center gap-2">
              <span className="text-accent-foreground">01</span>
              <span>Pattern Making</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-accent-foreground">02</span>
              <span>Sustainable Sourcing</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-accent-foreground">03</span>
              <span>Technical Assembly</span>
            </div>
          </div>
        </div>

        {}
        <div ref={visualRef} className={`md:col-span-5 relative min-h-[500px] md:min-h-[700px] transition-all duration-1000 delay-300 ease-out ${visualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute inset-0 rounded-lg bg-card/30 border border-foreground/5" />
          
          {archiveItems.map((item, i) => <div key={i} data-index={i} className={`group absolute ${item.position} ${item.size} ${item.rotation} transition-all duration-700 ease-out hover:!rotate-0 hover:-translate-y-6 hover:scale-105 cursor-crosshair z-[${10 + i}]`} style={{
          transitionDelay: visualVisible ? item.delay : "0ms",
          opacity: visualVisible ? 1 : 0
        }}>
              <div className="relative h-full w-full overflow-hidden rounded-lg bg-muted shadow-xl ring-1 ring-foreground/10">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                
                <div className="absolute inset-x-0 bottom-0 p-4 translate-y-4 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-[10px] text-accent-foreground uppercase tracking-widest">
                      {item.label}
                    </span>
                    <span className="font-serif italic text-lg text-white">
                      {item.category}
                    </span>
                  </div>
                </div>

                {}
                <div className="absolute top-3 right-3 h-2 w-2 border-t border-r border-white/40 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            </div>)}

          {}
          <div className="absolute -right-8 top-1/2 -translate-y-1/2 rotate-90 hidden lg:block">
            <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-foreground/20 whitespace-nowrap">
              ST_ARCHIVE_SYSTEM_V4.0
            </span>
          </div>

          {}
          <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 w-64 bg-card p-6 shadow-2xl ring-1 ring-foreground/10 transition-all duration-1000 ${visualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: "800ms"
        }}>
            <div className="relative">
              <LayoutGrid className="absolute -top-10 -left-2 size-8 text-accent-foreground/20" />
              <div className="flex flex-col gap-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-accent-foreground">
                  Technical Insight
                </span>
                <p className="font-serif italic text-base leading-snug text-foreground/90">
                  "Form follows function, but emotion dictates the silhouette."
                </p>
                <div className="mt-2 h-px w-8 bg-accent-foreground" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}